// src/pages/EditThemesPage.jsx
import { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import ProjectSelector from '../../components/admin/ProjectSelector';
import AddThemeForm from '../../components/admin/AddThemeForm';
import ThemeItem from '../../components/admin/ThemeItem';

const EditThemesPage = () => {
  const { user } = useAuth();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const roleNames = new Set(user?.roles?.map(r => r.name) || []);
  // ✅ Храним ID развёрнутых тем
  const [expandedThemes, setExpandedThemes] = useState(new Set());

  // Загрузка тем проекта
  const loadThemes = async (projectId) => {
    if (!projectId) return;

    setLoading(true);
    try {
      const res = await api.get(`/admin/analytics/themes/${projectId}`);
      const fetchedThemes = Array.isArray(res.data.themes) ? res.data.themes : [];
      setThemes(fetchedThemes);
      setSelectedProjectId(projectId);

      // ❌ Не сбрасываем expandedThemes при перезагрузке тем
      // Это позволяет сохранить состояние при обновлении
    } catch (err) {
      console.error('Ошибка загрузки тем:', err);
      setThemes([]);
    } finally {
      setLoading(false);
    }
  };

  // Обновить список тем (сохраняем состояние развёрнутых тем)
  const refreshThemes = () => {
    if (selectedProjectId) {
      loadThemes(selectedProjectId); // ✅ Перезагружаем данные, но expandedThemes остаётся
    }
    setShowAddModal(false);
  };

  // Переключение состояния развёрнутости
  const toggleExpanded = (themeId) => {
    setExpandedThemes((prev) => {
      const next = new Set(prev);
      if (next.has(themeId)) {
        next.delete(themeId);
      } else {
        next.add(themeId);
      }
      return next;
    });
  };

  // Удалить тему
  const handleDeleteTheme = (themeId) => {
    setThemes((prev) => prev.filter((t) => t.id !== themeId));
    setExpandedThemes((prev) => {
      const next = new Set(prev);
      next.delete(themeId);
      return next;
    });
  };

  // Перемещение стрелками (drag & drop)
  const moveTheme = async (themeId, direction) => {
    const currentIndex = themes.findIndex((t) => t.id === themeId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (
      (direction === 'up' && currentIndex <= 0) ||
      (direction === 'down' && currentIndex >= themes.length - 1)
    ) {
      return;
    }

    const newThemes = [...themes];
    const [moved] = newThemes.splice(currentIndex, 1);
    newThemes.splice(newIndex, 0, moved);

    setThemes(newThemes);

    try {
      await api.post('/admin/analytics/theme/reorder', newThemes.map((t) => t.id));
    } catch (err) {
      console.error('Ошибка при сохранении порядка:', err);
      window.showToast('Не удалось сохранить порядок тем');
      refreshThemes(); // откат при ошибке
    }
  };

  if (!roleNames.has('admin')) {
    return (
      <div className="main-content">
        <p>У вас нет прав для управления аналитикой.</p>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header mb-4">
        <h1>Редактирование тем</h1>
      </div>

      {/* Выбор проекта */}
      <ProjectSelector
        projects={user.projects}
        selectedProjectId={selectedProjectId}
        onChange={loadThemes}
      />

      {/* Контейнер тем */}
      <div id="theme-container">
        {selectedProjectId && (
          <>
            <h3>
              Проект:{' '}
              {user.projects.find((p) => p.id === selectedProjectId)?.name_text || 'Загрузка...'}
            </h3>

            <button
              type="button"
              className="btn btn-primary mb-4"
              onClick={() => setShowAddModal(true)}
            >
              Добавить тему
            </button>

            {loading ? (
              <p>Загрузка тем...</p>
            ) : themes.length === 0 ? (
              <p>Нет тем для этого проекта.</p>
            ) : (
              <ul className="list-group mb-5" id="themes-list">
                {themes.map((theme, index) => (
                  <li
                    key={theme.id}
                    data-theme-id={theme.id}
                    className="list-group-item"
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}
                  >
                    <div style={{ flex: 1 }}>
                      <ThemeItem
                        theme={theme}
                        projectId={selectedProjectId}
                        onUpdate={refreshThemes}
                        onDelete={handleDeleteTheme}
                        isExpanded={expandedThemes.has(theme.id)}
                        onToggleExpand={() => toggleExpanded(theme.id)}
                      />
                    </div>

                    <div className="btn-group-vertical" style={{ marginLeft: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => moveTheme(theme.id, 'up')}
                        disabled={index === 0}
                        aria-label="Переместить вверх"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => moveTheme(theme.id, 'down')}
                        disabled={index === themes.length - 1}
                        aria-label="Переместить вниз"
                      >
                        ↓
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {!selectedProjectId && <p>Выберите проект из списка.</p>}
      </div>

      {/* Модальное окно: Добавить тему */}
      {showAddModal && selectedProjectId && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ position: 'relative' }}>
                <h5 className="modal-title">Добавить новую тему</h5>
                <button
                  className="text-modal-close"
                  onClick={() => setShowAddModal(false)}
                  aria-label="Закрыть"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <AddThemeForm projectId={selectedProjectId} onAdd={refreshThemes} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Flash-сообщение (временно) */}
      <div
        id="flash-temp"
        className="alert alert-info d-none"
        style={{ display: 'none' }}
      >
        🔄 Идёт обновление, ожидайте проверки наличия фразы...
      </div>
    </div>
  );
};

export default EditThemesPage;
